<script lang="ts">
  import { Link } from 'svelte-routing';
  import { formatDailyAverageScore, formatDistance, scoreBandEmoji } from '@atlasguess/games';
  import DonorIcon from './DonorIcon.svelte';
  import FollowButton from './FollowButton.svelte';
  import { displayUnit } from '../lib/units';
  import type { LeaderboardRow } from '../lib/leaderboards';

  // Top-N table for daily leaderboard rows. Pure presentational —
  // owner component handles fetch + polling. The calling user's row
  // (if present) is highlighted via the optional `highlightUsername`
  // prop so they can spot themselves at a glance.
  //
  // Columns: rank, player (donor icon + username), score-cell ribbon
  // (one cell per round = emoji + score number, mirroring the share
  // string format), total score, average distance. The ribbon makes
  // run shape readable at a glance — a row of blues vs a row of reds
  // tells you "tight 5 rounds" vs "long-distance scattershot" without
  // having to read the totals.

  interface Props {
    rows: LeaderboardRow[];
    /** Username of the calling user — their row gets accent tint. */
    highlightUsername?: string | null;
  }

  let { rows, highlightUsername = null }: Props = $props();

  const formatAvg = (km: number): string => formatDistance(km, $displayUnit);
</script>

<div class="leaderboard-table-wrap">
  <table class="leaderboard-table">
    <thead>
      <tr>
        <th class="leaderboard-table__rank">#</th>
        <th>Player</th>
        <th class="leaderboard-table__follow" aria-label="Follow"></th>
        <th class="leaderboard-table__rounds">Rounds</th>
        <th class="leaderboard-table__num">Avg</th>
        <th class="leaderboard-table__num leaderboard-table__total-head">Score</th>
      </tr>
    </thead>
    <tbody>
      {#each rows as row (row.username + ':' + row.rank)}
        <tr class:leaderboard-table__row--mine={row.username === highlightUsername}>
          <td class="leaderboard-table__rank">{row.rank}</td>
          <td>
            <Link
              to={`/u/${row.username}`}
              getProps={() => ({ class: 'leaderboard-table__player' })}
            >
              <DonorIcon tier={row.donor_tier} size={40} />
              <span class="leaderboard-table__name">{row.username}</span>
            </Link>
          </td>
          <td class="leaderboard-table__follow">
            <!-- FollowButton renders nothing when the viewer is
                 anonymous or this is their own row, so the column
                 stays empty (but reserved-width) for those cases.
                 `compact` keeps the button at a steady text width
                 across hover so the column doesn't reflow when the
                 cursor moves between rows. -->
            <FollowButton username={row.username} size="sm" compact />
          </td>
          <td class="leaderboard-table__rounds">
            <ul class="leaderboard-table__score-list" aria-label="Per-round scores">
              {#each row.scores as score, i (i)}
                <li class="leaderboard-table__score-cell">
                  <span class="leaderboard-table__score-emoji" aria-hidden="true">
                    {scoreBandEmoji(score, i)}
                  </span>
                  <span class="leaderboard-table__score-num">{score}</span>
                </li>
              {/each}
            </ul>
          </td>
          <td class="leaderboard-table__num leaderboard-table__avg">
            {formatAvg(row.avg_km)}
          </td>
          <td class="leaderboard-table__num leaderboard-table__total">
            {formatDailyAverageScore(row.total_score, row.scores.length)}
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
</div>
